/**
 * One callback, in full — and the four things a person can do about it.
 *
 * The reason this is a sheet rather than a page: it is opened from a row to answer
 * a question about that row ("why hasn't this been called?"), and closing it should
 * put you back in the same scroll position with the same filters. The URL still
 * changes, so the view is linkable.
 *
 * The sections are ordered by the questions actually asked, in order:
 *   1. What is happening, and when. Including *why* that time.
 *   2. Who we are calling and which channel dials them.
 *   3. What the agent will know when it opens — the context we pass.
 *   4. What the retry rules are for this row, and which config level set each one.
 *   5. Every attempt, with 8loop's own verdict.
 *   6. What was posted to us in the first place.
 */

import { useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  CircleSlash,
  PhoneOutgoing,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { DateTime } from "luxon";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DetailField, DetailFields, DetailSection } from "@/components/ui/detail-fields";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { errorMessage, type CallbackAttempt, type CallbackDetail } from "@/lib/api";
import {
  DIAL_OUTCOME,
  END_REASON,
  minutesToWords,
  OUTCOME,
  relativeTime,
  RETRY_SOURCE,
  SCHEDULE_REASON,
  statusMeta,
  TONE_CLASS,
} from "@/lib/labels";
import { useCallback_, useCallbackAction, useMe } from "@/lib/queries";

export function CallbackSheet({
  accountId,
  callbackId,
  onClose,
}: {
  accountId: string;
  callbackId: string;
  onClose: () => void;
}) {
  const { data, isPending, isError, error, refetch } = useCallback_(accountId, callbackId);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[600px] gap-0 overflow-y-auto p-0 sm:max-w-[600px]">
        {isPending ? (
          <div className="space-y-4 p-8">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-72" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : isError || !data ? (
          <div className="p-8">
            <ErrorState
              title="Couldn't load this callback"
              description={errorMessage(error)}
              onRetry={() => refetch()}
            />
          </div>
        ) : (
          <Body accountId={accountId} detail={data} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function Body({ accountId, detail }: { accountId: string; detail: CallbackDetail }) {
  const c = detail.callback;
  const meta = statusMeta(c.status);
  const action = useCallbackAction(accountId);
  const { data: me } = useMe();
  const dryRun = me?.dry_run ?? false;
  const [rescheduleAt, setRescheduleAt] = useState("");

  const run = (body: Parameters<typeof action.mutate>[0]) =>
    action.mutate(body, {
      onSuccess: (res) => toast.success(res.message),
      onError: (err) => toast.error(errorMessage(err)),
    });

  const inFlight = c.status === "dialing" || c.status === "awaiting_outcome";
  const closed = c.status === "completed" || c.status === "cancelled" || c.status === "not_a_callback";

  return (
    <>
      <SheetHeader className="px-8 pt-8">
        <SheetTitle className="flex items-center gap-2 font-mono text-lg">
          {c.phone_display}
          {c.contact_name && (
            <span className="font-sans text-sm font-normal text-muted-foreground">
              {c.contact_name}
            </span>
          )}
        </SheetTitle>
        <SheetDescription className={TONE_CLASS[meta.tone]}>
          {meta.label} — {meta.hint}
        </SheetDescription>
      </SheetHeader>

      {/*
        Red for something that went wrong, plain for something the account chose.

        `not_a_callback` carries an `attention_reason` that is a policy sentence, not a failure —
        "they asked for a callback but named no time, and this account only calls back when a
        time was captured". `STATUS` in labels.ts already tones that status muted; a destructive
        band above it undid that and made a working setting look like an incident.
      */}
      {c.attention_reason && (
        <div className="px-8 pt-4">
          <Alert variant={c.status === "not_a_callback" ? "default" : "destructive"}>
            <AlertTriangle className="size-4" />
            <AlertDescription>{c.attention_reason}</AlertDescription>
          </Alert>
        </div>
      )}
      {c.last_error_message && !c.attention_reason && (
        <div className="px-8 pt-4">
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{c.last_error_message}</AlertDescription>
          </Alert>
        </div>
      )}

      <div className="space-y-4 px-8 py-6">
        {/* ------------------------------------------------------- actions -- */}
        <div className="flex flex-wrap items-center gap-2">
          {/*
            Call now rings a real person's phone within seconds, from a button sitting
            one row-click away — so it asks first. Except in dry run, where nothing is
            dialled and a confirmation would only teach people to click through it.
          */}
          <Hint
            text={
              inFlight
                ? "A call for this callback is already in progress."
                : closed
                  ? "This callback is closed."
                  : dryRun
                    ? "Dry run: this records an attempt without dialling anybody."
                    : "Place the call now, ignoring its scheduled time."
            }
          >
            {dryRun ? (
              <Button
                size="sm"
                onClick={() => run({ callbackId: c.id, action: "dial_now" })}
                disabled={action.isPending || inFlight || closed}
              >
                <PhoneOutgoing className="size-3.5" />
                Call now
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={action.isPending || inFlight || closed}>
                    <PhoneOutgoing className="size-3.5" />
                    Call now
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Call {c.phone_display} now?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This places the call immediately, ignoring both its scheduled time
                      {c.scheduled_for_local ? ` of ${c.scheduled_for_local}` : ""} and this
                      account's calling hours. Their phone rings within seconds and the attempt
                      counts against the cap. It cannot be recalled.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Don't call</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => run({ callbackId: c.id, action: "dial_now" })}
                    >
                      Call now
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </Hint>

          <Hint
            text={
              c.status === "awaiting_outcome"
                ? "Ask 8loop again, right now, how the call ended."
                : "Only useful while we are waiting on an outcome from 8loop."
            }
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => run({ callbackId: c.id, action: "recheck" })}
              disabled={action.isPending || c.status !== "awaiting_outcome"}
            >
              <RefreshCw className="size-3.5" />
              Check outcome now
            </Button>
          </Hint>

          {/*
            Cancelling closes the callback for good — there is no reopen, and the person
            who asked to be rung back simply never is. That earns both the destructive
            colour and a confirmation naming the number.
          */}
          <Hint
            text={
              closed
                ? "This callback is already closed."
                : "Close this callback for good. Nothing further is dialled."
            }
          >
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" disabled={action.isPending || closed}>
                  <CircleSlash className="size-3.5" />
                  Cancel
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel the callback to {c.phone_display}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Nothing further is dialled for this request and it cannot be reopened.
                    {c.outcome_label ? ` They were queued because: ${c.outcome_label}.` : ""} If the
                    tenant's agent reports the same outcome again, a new callback appears.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep it</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => run({ callbackId: c.id, action: "cancel" })}
                  >
                    Cancel the callback
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </Hint>
        </div>

        {!closed && !inFlight && (
          <div className="flex items-end gap-2 rounded-lg border border-border p-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="reschedule" className="text-xs">
                Move to a different time
                <span className="ml-1 font-normal text-muted-foreground">
                  ({c.account_timezone})
                </span>
              </Label>
              <Input
                id="reschedule"
                type="datetime-local"
                value={rescheduleAt}
                onChange={(e) => setRescheduleAt(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!rescheduleAt || action.isPending}
              onClick={() => {
                run({ callbackId: c.id, action: "reschedule", at: rescheduleAt });
                setRescheduleAt("");
              }}
            >
              <CalendarClock className="size-3.5" />
              Move
            </Button>
          </div>
        )}

        {/* -------------------------------------------------------- timing -- */}
        <DetailSection title="Timing">
          <DetailFields>
            <DetailField label="Scheduled for">
              {c.scheduled_for_local ? (
                <>
                  {c.scheduled_for_local}
                  <span className="text-xs text-muted-foreground">
                    {relativeTime(c.scheduled_for)}
                  </span>
                  {c.overdue && <Badge variant="outline" className="text-warning">overdue</Badge>}
                </>
              ) : null}
            </DetailField>
            <DetailField label="Why that time">
              {c.schedule_reason ? (SCHEDULE_REASON[c.schedule_reason] ?? c.schedule_reason) : null}
            </DetailField>
            <DetailField label="They asked for">
              {c.requested_at_local ?? (c.requested_raw ? <em>{c.requested_raw}</em> : null)}
            </DetailField>
            <DetailField label="Reason for the callback">{c.outcome_label}</DetailField>
            <DetailField label="Timezone">{c.account_timezone}</DetailField>
          </DetailFields>
        </DetailSection>

        {/* --------------------------------------------------------- route -- */}
        <DetailSection title="Who dials, and from where">
          <DetailFields>
            <DetailField label="Original agent">{c.source_agent_name ?? c.source_agent_id}</DetailField>
            <DetailField label="Original channel" mono>
              {c.source_channel_id}
            </DetailField>
            <DetailField label="Callback channel">
              {/*
                Since routing became identity there is usually nothing configured
                *and nothing wrong*: the callback goes back out on the channel it
                came in on. So the absence of an override is not the failure case,
                and saying so was an outright lie on calls that had already been
                placed. The only real failure is having no routing identifier to
                dial by, and that gets its own wording below.
              */}
              {c.dial_channel_label ? (
                <>
                  {c.dial_channel_label}
                  <span className="text-xs text-muted-foreground">
                    {c.dial_is_override ? "sent here by an override" : "the same channel it came in on"}
                  </span>
                </>
              ) : c.dial_routing_identifier ? (
                <span className="text-muted-foreground">
                  the same channel it came in on
                  {c.source_channel_id ? ` (channel ${c.source_channel_id})` : ""} — not in the
                  cached channel list, so there is no name to show
                </span>
              ) : (
                <span className="text-destructive">
                  no channel resolved — this will not be dialled until this agent's channels can
                  reach the number: Configuration → Agents, on that agent's page
                </span>
              )}
            </DetailField>
            <DetailField label="Routing identifier" mono>
              {c.dial_routing_identifier}
            </DetailField>
          </DetailFields>
        </DetailSection>

        {/* ------------------------------------------------------- context -- */}
        <DetailSection
          title="What the agent will know"
          contentClassName="border-border-subtle border-t px-5 py-3"
        >
          {c.context && Object.keys(c.context).length > 0 ? (
            <dl className="space-y-2">
              {Object.entries(c.context).map(([key, value]) => (
                <div key={key}>
                  <dt className="font-mono text-[11px] text-muted-foreground">{key}</dt>
                  <dd className="text-sm">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="py-1 text-sm text-muted-foreground">
              No conversation fields are being passed, so this call will open cold. Context is set
              per agent, not per account: open the agent this call came from under Configuration →
              Agents, and block 2 of its hook setup is where <span className="font-mono">
              callback_context</span> and the <span className="font-mono">previous_…</span> fields
              are switched on. They take effect on the next call the hook posts.
            </p>
          )}
        </DetailSection>

        {/* ------------------------------------------------------- outcome -- */}
        {(c.eightloop_call_id || c.last_end_reason || c.verify_after) && (
          <DetailSection title="How the last call ended">
            <DetailFields>
              <DetailField label="8loop's end reason">
                {c.last_end_reason ? (
                  <>
                    {END_REASON[c.last_end_reason] ?? c.last_end_reason}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {c.last_end_reason}
                    </span>
                  </>
                ) : null}
              </DetailField>
              {/* Both of these are internal slugs on the wire. `never_spoke` and
                  `provider_error` are not sentences, and this row is read by people who
                  did not write the mapping — so the words come from labels.ts and the
                  slug stays underneath for anyone comparing against the database. */}
              <DetailField label="What we made of it">
                {c.dial_outcome ? (
                  <>
                    {DIAL_OUTCOME[c.dial_outcome] ?? c.dial_outcome}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {c.dial_outcome}
                    </span>
                  </>
                ) : null}
              </DetailField>
              <DetailField label="Tenant's own webhook said">
                {c.reported_outcome ? (
                  <>
                    {OUTCOME[c.reported_outcome] ?? c.reported_outcome}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {c.reported_outcome}
                    </span>
                  </>
                ) : null}
              </DetailField>
              <DetailField label="Next outcome check">
                {c.verify_after && c.status === "awaiting_outcome" ? (
                  <>
                    {relativeTime(c.verify_after)}
                    <span className="text-xs text-muted-foreground">
                      we ask 8loop again on a timer until it answers
                    </span>
                  </>
                ) : null}
              </DetailField>
              <DetailField label="8loop call id" mono>
                {c.eightloop_call_id}
              </DetailField>
            </DetailFields>
          </DetailSection>
        )}

        {/* --------------------------------------------------------- retry -- */}
        <DetailSection title="Retry rules for this row">
          <DetailFields>
            {/* "from account" / "from outcome" was the server's own enum leaking through.
                It names neither the setting nor the screen it lives on, so somebody who
                disagrees with the number has nowhere to go. */}
            <DetailField label="Retry after">
              {minutesToWords(detail.retry.delay_minutes)}
              <span className="text-xs text-muted-foreground">
                from {RETRY_SOURCE[detail.retry.delay_from] ?? detail.retry.delay_from}
              </span>
            </DetailField>
            <DetailField label="Attempt cap">
              {detail.retry.max_attempts} per number on this channel
              <span className="text-xs text-muted-foreground">
                from{" "}
                {RETRY_SOURCE[detail.retry.max_attempts_from] ?? detail.retry.max_attempts_from}
              </span>
            </DetailField>
            <DetailField label="Attempts so far">
              {c.attempts} of {detail.retry.max_attempts}
            </DetailField>
            <DetailField label="Counted over">
              the last {detail.retry.lookback_days} days
            </DetailField>
          </DetailFields>
        </DetailSection>

        {/* ------------------------------------------------------ attempts -- */}
        <DetailSection
          title={`Calls placed (${detail.attempts.length})`}
          contentClassName="border-border-subtle border-t px-5 py-3"
        >
          {detail.attempts.length === 0 ? (
            <p className="py-1 text-sm text-muted-foreground">
              Nothing has been dialled yet.
            </p>
          ) : (
            <ol className="space-y-3">
              {detail.attempts.map((a) => (
                <Attempt key={a.attempt_no} attempt={a} timezone={c.account_timezone} />
              ))}
            </ol>
          )}
        </DetailSection>

        {/* -------------------------------------------------------- ingest -- */}
        <DetailSection
          title={`What was posted to us (${detail.events.length})`}
          contentClassName="border-border-subtle border-t px-5 py-3"
        >
          {detail.events.length === 0 ? (
            <p className="py-1 text-sm text-muted-foreground">
              No ingest events are stored for this callback.
            </p>
          ) : (
            <ol className="space-y-3">
              {detail.events.map((e) => (
                <li key={e.id} className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={e.accepted ? "text-success" : "text-destructive"}>
                      {e.accepted ? "accepted" : "rejected"}
                    </span>
                    <span className="text-muted-foreground">
                      {localTime(e.received_at, c.account_timezone)}
                    </span>
                  </div>
                  {e.rejected_reason && (
                    <p className="text-xs text-destructive">{e.rejected_reason}</p>
                  )}
                  <pre className="max-h-48 overflow-auto rounded-md bg-muted p-2 font-mono text-[11px] leading-relaxed">
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                </li>
              ))}
            </ol>
          )}
        </DetailSection>

        {/* ------------------------------------------------------- history -- */}
        {detail.history.length > 0 && (
          <DetailSection
            title={`Other callbacks for this number (${detail.history.length})`}
            contentClassName="border-border-subtle border-t px-5 py-3"
          >
            <ul className="space-y-2">
              {detail.history.map((h) => {
                const hm = statusMeta(h.status);
                return (
                  <li key={h.id} className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-muted-foreground">{h.scheduled_for_local ?? "—"}</span>
                    <span className="min-w-0 flex-1 truncate">{h.outcome_label}</span>
                    <span className={TONE_CLASS[hm.tone]}>{hm.label}</span>
                  </li>
                );
              })}
            </ul>
          </DetailSection>
        )}
      </div>
    </>
  );
}

/**
 * A tooltip that still works when the control inside it is disabled.
 *
 * `title` on a disabled `<button>` never appears: the browser stops dispatching
 * pointer events to it, and `buttonVariants` adds `disabled:pointer-events-none` on
 * top of that. Which is exactly backwards — the moment a button is dead is the moment
 * its explanation matters, and "Call now" going grey with no reason is the whole
 * complaint. Putting the `title` on a wrapper the pointer *can* reach fixes it, because
 * the dead child lets the pointer through to its parent.
 */
function Hint({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <span title={text} className="inline-flex">
      {children}
    </span>
  );
}

/**
 * One dial: what we sent, and whether 8loop took it.
 *
 * How the call *ended* is not here on purpose — we store one end reason per
 * callback, not per attempt, so it is stated once in "How the last call ended"
 * above rather than pinned to a row it might not describe.
 */
function Attempt({ attempt, timezone }: { attempt: CallbackAttempt; timezone: string }) {
  return (
    <li className="space-y-1 border-l-2 border-border pl-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">Attempt {attempt.attempt_no}</span>
        <span className="text-muted-foreground">{localTime(attempt.started_at, timezone)}</span>
        <span className={attempt.ok ? "text-success" : "text-destructive"}>
          {attempt.ok ? "accepted by 8loop" : "refused"}
        </span>
        {attempt.idempotent_replay && (
          <Badge variant="outline" className="text-[10px]">
            replay — no second call
          </Badge>
        )}
      </div>
      {attempt.eightloop_call_id && (
        <p className="font-mono text-[11px] text-muted-foreground">
          call {attempt.eightloop_call_id}
        </p>
      )}
      {attempt.error_message && (
        <p className="text-xs text-destructive">
          {attempt.error_message}
          {attempt.error_code && (
            <span className="ml-1 font-mono text-[11px]">({attempt.error_code})</span>
          )}
        </p>
      )}
    </li>
  );
}

/** Attempt and event timestamps are raw ISO, so these are localised here. */
function localTime(iso: string, timezone: string): string {
  const dt = DateTime.fromISO(iso).setZone(timezone);
  return dt.isValid ? dt.toFormat("d LLL, HH:mm:ss") : iso;
}
